/**
 * Wall-clock recording duration with pause accounting.
 * Timestamps are passed in (ms since epoch) so callers control the clock and tests are trivial.
 */

export type RecordingClock = {
  /** Start (or restart) the clock. `atMs` may be in the past, e.g. resuming an interrupted draft. */
  start: (atMs: number) => void;
  pause: (atMs: number) => void;
  resume: (atMs: number) => void;
  stop: () => void;
  isRunning: () => boolean;
  durationMs: (nowMs: number) => number;
};

export function createRecordingClock(): RecordingClock {
  let startedAt: number | null = null;
  let pausedAt: number | null = null;
  let accumulatedPausedMs = 0;

  return {
    start(atMs: number) {
      startedAt = atMs;
      pausedAt = null;
      accumulatedPausedMs = 0;
    },
    pause(atMs: number) {
      if (startedAt == null || pausedAt != null) return;
      pausedAt = atMs;
    },
    resume(atMs: number) {
      if (pausedAt == null) return;
      accumulatedPausedMs += atMs - pausedAt;
      pausedAt = null;
    },
    stop() {
      startedAt = null;
      pausedAt = null;
      accumulatedPausedMs = 0;
    },
    isRunning() {
      return startedAt != null;
    },
    durationMs(nowMs: number) {
      if (startedAt == null) return 0;
      const effectiveNow = pausedAt != null ? pausedAt : nowMs;
      return Math.max(0, effectiveNow - startedAt - accumulatedPausedMs);
    },
  };
}
